import { useEffect, useState } from 'react';
import { chipColours } from '../../lib/colour';
import { useTheme } from '../../theme/ThemeProvider';
import type { CollectionDef } from './collections';
import type { UpdateVocabBody, VocabRow } from './settings-api';
import { usageLabel, type Usage } from './usage';
import { canWrite, disabledReason, type WriteGate } from './WriteGate';

/**
 * ONE VOCABULARY ROW: label · code · colour · active · usage.
 *
 * 🔴🔴 TWO RULES ARE STRUCTURAL HERE, NOT REMEMBERED:
 *
 * 1. **`code` HAS NO INPUT.** Not a disabled input — no input at all. `ticket.status`,
 *    `ticket_status_history.from_status/to_status` and `status_transition` all reference the code BY
 *    VALUE, so renaming it does not rename anything: it re-points every historical row at a meaning
 *    that did not exist when the row was written. "APFS-441 moved to `code_review` on 3 July" would
 *    quietly become a claim about whatever `code_review` was renamed into. The label is freely
 *    editable precisely so nobody ever needs the code to be.
 *
 * 2. **THERE IS NO DELETE.** Only Deactivate. The API README's rule is "deactivate, never delete" and
 *    the FK enforces it anyway — but the reason a delete button is absent rather than disabled is
 *    that a disabled delete invites someone to make it work. `is_active = false` hides a value from
 *    every picker while every existing reference stays readable, which is the whole requirement.
 *
 * ⭐ The usage count sits NEXT TO the toggle, so the consequence is legible before the click rather
 * than explained afterwards by a 409.
 */
export function VocabRowItem({
  row,
  def,
  gate,
  usage,
  index,
  count,
  busy,
  error,
  onSave,
  onToggleActive,
  onMove,
  onDragStart,
  onDragOver,
  onDrop,
}: {
  row: VocabRow;
  def: CollectionDef;
  gate: WriteGate;
  usage: Usage;
  index: number;
  count: number;
  busy: boolean;
  /** The API's own refusal for THIS row, rendered verbatim. */
  error: string | null;
  onSave: (patch: UpdateVocabBody) => void;
  onToggleActive: () => void;
  onMove: (delta: number) => void;
  onDragStart: () => void;
  onDragOver: (event: React.DragEvent) => void;
  onDrop: () => void;
}) {
  const { resolved } = useTheme();
  const editable = canWrite(gate);
  const why = disabledReason(gate);

  const [label, setLabel] = useState(row.label);
  const [colour, setColour] = useState(row.colour ?? '');

  // ⚠️ Re-seed when the SERVER's value changes — otherwise a successful save leaves the field showing
  // the draft, which is indistinguishable from a save that did nothing.
  useEffect(() => setLabel(row.label), [row.label]);
  useEffect(() => setColour(row.colour ?? ''), [row.colour]);

  const dirty = label !== row.label || (colour || null) !== (row.colour ?? null);
  const swatch = chipColours(colour || row.colour, resolved === 'dark');

  return (
    <li
      draggable={editable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      aria-label={`${row.label} (${row.code})`}
      className="border-b border-[color:var(--color-line)] px-3 py-3 last:border-b-0 sm:px-4"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        {/* Order controls. ⭐ Buttons as well as drag: a drag is unreachable by keyboard and awkward
            with a thumb, and this list is 26 rows long on a 390px screen. */}
        <div className="flex shrink-0 items-center gap-1">
          <span
            aria-hidden="true"
            className="cursor-grab text-[color:var(--color-ink-subtle)] select-none"
            title={editable ? 'Drag to reorder' : why}
          >
            ⠿
          </span>
          <button
            type="button"
            disabled={!editable || index === 0}
            title={why ?? 'Move up'}
            onClick={() => onMove(-1)}
            aria-label={`Move ${row.label} up`}
            className="size-7 rounded border border-[color:var(--color-line)] text-xs disabled:opacity-40"
          >
            ↑
          </button>
          <button
            type="button"
            disabled={!editable || index === count - 1}
            title={why ?? 'Move down'}
            onClick={() => onMove(1)}
            aria-label={`Move ${row.label} down`}
            className="size-7 rounded border border-[color:var(--color-line)] text-xs disabled:opacity-40"
          >
            ↓
          </button>
        </div>

        <div className="min-w-0 flex-1">
          <label className="sr-only" htmlFor={`label-${def.name}-${row.code}`}>
            Label for {row.code}
          </label>
          <input
            id={`label-${def.name}-${row.code}`}
            value={label}
            disabled={!editable || busy}
            title={why}
            onChange={(event) => setLabel(event.target.value)}
            className="w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1.5 text-sm disabled:opacity-70"
          />

          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[color:var(--color-ink-muted)]">
            {/*
              🔴 THE CODE, AS TEXT. There is no input here and there must never be one — see the file
              header. The padlock is not decoration: it is the answer to "why can I edit the label and
              not this?", given where the question is asked.
            */}
            <span className="font-mono" title="Immutable — tickets and history reference this value">
              <span aria-hidden="true">🔒 </span>
              <span className="sr-only">Code, immutable after creation: </span>
              {row.code}
            </span>

            {row.is_initial ? (
              <span className="rounded bg-[color:var(--color-line)] px-1.5 py-0.5 font-semibold">
                initial status
              </span>
            ) : null}
            {row.is_final ? (
              <span className="rounded bg-[color:var(--color-line)] px-1.5 py-0.5">final</span>
            ) : null}
            {row.is_subtask ? (
              <span className="rounded bg-[color:var(--color-line)] px-1.5 py-0.5">sub-task type</span>
            ) : null}
            {row.lane ? <span>lane: {row.lane}</span> : null}
          </div>
        </div>

        {def.hasColour ? (
          <div className="flex shrink-0 items-center gap-2">
            <span
              aria-hidden="true"
              className="inline-block size-6 shrink-0 rounded border border-[color:var(--color-line)]"
              style={{ background: swatch.background }}
            />
            <label className="sr-only" htmlFor={`colour-${def.name}-${row.code}`}>
              Colour for {row.label}
            </label>
            <input
              id={`colour-${def.name}-${row.code}`}
              value={colour}
              placeholder="#rrggbb"
              disabled={!editable || busy}
              title={why}
              onChange={(event) => setColour(event.target.value)}
              className="w-24 rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1.5 font-mono text-xs disabled:opacity-70"
            />
          </div>
        ) : null}

        <div className="flex shrink-0 items-center justify-between gap-3 sm:justify-end">
          {/*
            ⭐ THE COUNT SITS NEXT TO THE TOGGLE. This is the point of the requirement: "47 tickets"
            here means the consequence is read before the decision. `usage unknown` is printed as
            itself and never as 0 — see usage.ts.
          */}
          <span
            className="text-xs text-[color:var(--color-ink-muted)]"
            title={usage.kind === 'unavailable' || usage.kind === 'failed' ? usage.why : undefined}
          >
            {usageLabel(usage)}
          </span>

          <button
            type="button"
            role="switch"
            aria-checked={row.is_active}
            disabled={!editable || busy}
            title={why ?? (row.is_active ? 'Deactivate' : 'Reactivate')}
            onClick={onToggleActive}
            className={`h-8 rounded-full px-3 text-xs font-semibold disabled:opacity-50 ${
              row.is_active
                ? 'bg-[color:var(--color-ink)] text-[color:var(--color-surface)]'
                : 'border border-dashed border-[color:var(--color-line)] text-[color:var(--color-ink-muted)]'
            }`}
          >
            {row.is_active ? 'Active' : 'Inactive'}
          </button>
        </div>
      </div>

      {dirty ? (
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            disabled={!editable || busy}
            onClick={() => onSave({ label, colour: colour || null })}
            className="h-8 rounded bg-[color:var(--color-accent)] px-3 text-xs font-semibold text-[color:var(--color-on-accent)] disabled:opacity-50"
          >
            Save changes
          </button>
          <button
            type="button"
            onClick={() => {
              setLabel(row.label);
              setColour(row.colour ?? '');
            }}
            className="h-8 rounded border border-[color:var(--color-line)] px-3 text-xs"
          >
            Revert
          </button>
          <span className="text-xs text-[color:var(--color-ink-muted)]">
            Label and colour only — the code cannot change.
          </span>
        </div>
      ) : null}

      {/*
        🔴 THE API'S OWN REASON, VERBATIM. The API refuses to deactivate the last active status, the
        initial status, or one still referenced by an active transition — and those rules live THERE.
        This screen does not re-implement them to grey a toggle out: a second copy of a rule drifts,
        and the drifted copy always looks right.
      */}
      {error ? (
        <p
          role="alert"
          className="mt-2 rounded border border-red-300 bg-red-50 px-2 py-1.5 text-xs text-red-900 dark:border-red-900/60 dark:bg-red-950/50 dark:text-red-100"
        >
          {error}
        </p>
      ) : null}
    </li>
  );
}
